
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslations } from "@/hooks/useTranslations";

interface GymPhotoUploadProps {
  userId: string;
  gymPhotoUrl: string;
  onGymPhotoUpdate: (url: string) => void;
}

export const GymPhotoUpload = ({ userId, gymPhotoUrl, onGymPhotoUpdate }: GymPhotoUploadProps) => {
  const { toast } = useToast();
  const { t } = useTranslations("profile");

  const handleGymPhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) return;
      
      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const filePath = `${userId}/gym.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ gym_photo_url: publicUrl })
        .eq('id', userId);

      if (updateError) throw updateError;

      onGymPhotoUpdate(publicUrl);
      
      toast({
        title: t("photoSuccess"),
        description: t("photoSuccessDesc"),
      });
    } catch (error) {
      toast({
        title: t("photoError"),
        description: t("photoErrorDesc"),
        variant: "destructive"
      });
    }
  };

  const handleDeleteGymPhoto = async () => {
    try {
      if (!gymPhotoUrl) return;

      const url = new URL(gymPhotoUrl);
      const pathSegments = url.pathname.split('/');
      const filePath = pathSegments[pathSegments.length - 1];

      const { error: deleteError } = await supabase.storage
        .from('avatars')
        .remove([filePath]);

      if (deleteError) throw deleteError;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ gym_photo_url: null })
        .eq('id', userId);

      if (updateError) throw updateError;

      onGymPhotoUpdate("");

      toast({
        title: t("photoRemoved"),
        description: t("photoRemovedDesc"),
      });
    } catch (error) {
      toast({
        title: t("photoError"),
        description: t("photoErrorDesc"),
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-4">
      <Label>{t("gymPhoto")}</Label>
      <div className="flex flex-col sm:flex-row items-center gap-4">
        {gymPhotoUrl && (
          <img 
            src={gymPhotoUrl} 
            alt="Ginásio"
            className="h-24 w-24 object-cover rounded-lg"
          />
        )}
        <div className="flex flex-wrap gap-2 justify-center w-full">
          <Input
            type="file"
            accept="image/*"
            onChange={handleGymPhotoUpload}
            className="hidden"
            id="gym-photo-upload"
          />
          <Button
            variant="outline"
            onClick={() => document.getElementById('gym-photo-upload')?.click()}
            className="flex-1 min-w-[140px] max-w-[200px] dark:text-white"
          >
            <Upload className="mr-2 h-4 w-4" />
            {t("uploadPhoto")}
          </Button>
          {gymPhotoUrl && (
            <Button
              variant="destructive"
              onClick={handleDeleteGymPhoto}
              className="flex-1 min-w-[140px] max-w-[200px]"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t("removePhoto")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
